// apps/admin/src/components/icon.tsx
//
// P5 UI Polish W0 T4 — Lucide icon wrapper (spec §1.4).
//
// Why a wrapper? Two reasons:
//   (1) Tree-shake control. lucide-react exports ~1000 icons; we only
//       use ~20-30. Re-exporting from a central file lets the bundler
//       see exactly which icons are referenced anywhere in the app.
//   (2) Single import surface for call-sites:
//
//         import { Icon } from '@/components/icon';
//         <Icon name="search" size={16} />
//
//       Cleaner than importing each icon individually at every use.
//
// Adding an icon: import it from 'lucide-react', add it to the `icons`
// registry below with a kebab-case name. Type narrowing on Icon's
// `name` prop will surface the new name to call-sites without re-typing.

import {
  Search,
  Bell,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Menu,
  X,
  Plus,
  Check,
  AlertCircle,
  CheckCircle2,
  PanelLeftClose,
  PanelLeftOpen,
  LogOut,
  User,
  Settings,
  Calendar,
  Image as ImageIcon,
  Film,
  Layers,
  GripVertical,
  AlignStartHorizontal,
  AlignCenterHorizontal,
  AlignEndHorizontal,
  AlignStartVertical,
  AlignCenterVertical,
  AlignEndVertical,
  AlignHorizontalDistributeCenter,
  AlignVerticalDistributeCenter,
  Undo2,
  Redo2,
  Clock,
  // SP1 — expanded set: device types, status, lifecycle, dashboard, general.
  Monitor,
  Tv,
  Tablet,
  Projector,
  Grid3x3,
  MonitorX,
  PlayCircle,
  CircleDot,
  AlertTriangle,
  Info,
  XCircle,
  Wifi,
  WifiOff,
  MapPin,
  Trash2,
  Wrench,
  MoreHorizontal,
  ChevronUp,
  ExternalLink,
  RefreshCw,
  Building2,
  Power,
  Languages,
  Shield,
  AtSign,
  type LucideIcon,
} from 'lucide-react';

export const icons = {
  'search': Search,
  'bell': Bell,
  'chevron-down': ChevronDown,
  'chevron-right': ChevronRight,
  'chevron-left': ChevronLeft,
  'menu': Menu,
  'x': X,
  'plus': Plus,
  'check': Check,
  'alert-circle': AlertCircle,
  'check-circle': CheckCircle2,
  'panel-left-close': PanelLeftClose,
  'panel-left-open': PanelLeftOpen,
  'log-out': LogOut,
  'user': User,
  'settings': Settings,
  'calendar': Calendar,
  'image': ImageIcon,
  'film': Film,
  'layers': Layers,
  'grip-vertical': GripVertical,
  'align-start-horizontal': AlignStartHorizontal,
  'align-center-horizontal': AlignCenterHorizontal,
  'align-end-horizontal': AlignEndHorizontal,
  'align-start-vertical': AlignStartVertical,
  'align-center-vertical': AlignCenterVertical,
  'align-end-vertical': AlignEndVertical,
  'align-horizontal-distribute-center': AlignHorizontalDistributeCenter,
  'align-vertical-distribute-center': AlignVerticalDistributeCenter,
  'undo-2': Undo2,
  'redo-2': Redo2,
  'clock': Clock,
  'monitor': Monitor,
  'tv': Tv,
  'tablet': Tablet,
  'projector': Projector,
  'grid-3x3': Grid3x3,
  'monitor-x': MonitorX,
  'play-circle': PlayCircle,
  'circle-dot': CircleDot,
  'alert-triangle': AlertTriangle,
  'info': Info,
  'x-circle': XCircle,
  'wifi': Wifi,
  'wifi-off': WifiOff,
  'map-pin': MapPin,
  'trash-2': Trash2,
  'wrench': Wrench,
  'more-horizontal': MoreHorizontal,
  'chevron-up': ChevronUp,
  'external-link': ExternalLink,
  'refresh-cw': RefreshCw,
  'building-2': Building2,
  'power': Power,
  'languages': Languages,
  'shield': Shield,
  'at-sign': AtSign,
} as const satisfies Record<string, LucideIcon>;

export type IconName = keyof typeof icons;

// SP1 semantic maps — telling icons for screen TYPE and live STATUS.
// String-keyed with fallbacks (the DeviceType/status enums land in SP2);
// these are the single source of truth for type/status iconography.
export type StatusToneName = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

const DEVICE_TYPE_ICONS: Record<string, IconName> = {
  kiosk: 'monitor',
  tv: 'tv',
  screen: 'monitor',
  signage: 'monitor',
  led: 'grid-3x3',
  tablet: 'tablet',
  projector: 'projector',
};

/** Icon name for a screen/device type; unknown → a neutral monitor-x. */
export function deviceTypeIcon(type: string | null | undefined): IconName {
  return DEVICE_TYPE_ICONS[(type ?? '').toLowerCase()] ?? 'monitor-x';
}

const STATUS_TONES: Record<string, { icon: IconName; tone: StatusToneName }> = {
  active: { icon: 'check-circle', tone: 'success' },
  online: { icon: 'check-circle', tone: 'success' },
  running: { icon: 'play-circle', tone: 'info' },
  idle: { icon: 'circle-dot', tone: 'warning' },
  stale: { icon: 'alert-triangle', tone: 'warning' },
  maintenance: { icon: 'wrench', tone: 'warning' },
  down: { icon: 'x-circle', tone: 'danger' },
  offline: { icon: 'wifi-off', tone: 'danger' },
  broken: { icon: 'x-circle', tone: 'danger' },
  end_of_life: { icon: 'x-circle', tone: 'neutral' },
};

/** Icon + semantic tone for a live/lifecycle status; unknown → neutral dot. */
export function statusTone(status: string | null | undefined): {
  icon: IconName;
  tone: StatusToneName;
} {
  return STATUS_TONES[(status ?? '').toLowerCase()] ?? { icon: 'circle-dot', tone: 'neutral' };
}

export interface IconProps {
  name: IconName;
  size?: number;
  color?: string;
  className?: string;
  'data-testid'?: string;
  'aria-label'?: string;
}

export function Icon({
  name,
  size = 16,
  color,
  className,
  ...rest
}: IconProps): JSX.Element {
  const Component = icons[name];
  return <Component size={size} color={color} className={className} {...rest} />;
}
